import { useEffect, useState } from "react";
import { ApiError } from "./api";

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Run an async fetch, tracking loading/error and exposing reload(). A 401 reloads the page so the
 * app re-resolves identity (an expired session lands back on the landing/login). `deps` re-runs it.
 */
export function useAsync<T>(
  fn: () => Promise<T>,
  deps: unknown[],
  intervalMs?: number,
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // Optional background auto-refresh — re-runs the fetch on a timer without blanking the view
  // (components gate the spinner on `loading && !data`, so a refresh doesn't flicker).
  useEffect(() => {
    if (!intervalMs) return;
    const id = setInterval(() => setNonce((n) => n + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 401) {
          location.reload();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, loading, error, reload: () => setNonce((n) => n + 1) };
}
